import type * as React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO } from 'date-fns';
import type { V2DateElement } from '../../../types/V2Types';
import type { V2ElementRendererProps } from './elementProps';
import { todayIso } from '../../../Utils/dateUtils';

const ISO_DATE_FORMAT = 'yyyy-MM-dd';

function isoToDate(iso: string | undefined): Date | null {
	if (!iso) return null;
	try {
		const d = parseISO(iso);
		return Number.isNaN(d.getTime()) ? null : d;
	} catch {
		return null;
	}
}

export function DateElement({ element, value, errors, disabled, onChange }: V2ElementRendererProps<V2DateElement>) {
	const stringValue = typeof value === 'string' ? value : '';
	const errorMessage = errors[0]?.message;

	// Future dates are never valid for a DOB; respect a tighter playbook bound
	// if one is supplied. Playbook bounds arrive as `yyyy-MM-dd` strings; MUI X
	// expects `Date` objects.
	const maxIso = element.maxDate && element.maxDate < todayIso() ? element.maxDate : todayIso();
	const minDate = isoToDate(element.minDate) ?? undefined;
	const maxDate = isoToDate(maxIso) ?? undefined;
	const dateValue = isoToDate(stringValue);

	return (
		<DatePicker
			value={dateValue}
			onChange={(newValue: Date | null) => {
				if (!newValue || Number.isNaN(newValue.getTime())) {
					onChange('');
					return;
				}
				onChange(format(newValue, ISO_DATE_FORMAT));
			}}
			label={`${element.label} (mm/dd/yyyy)`}
			format="MM/dd/yyyy"
			minDate={minDate}
			maxDate={maxDate}
			disabled={disabled}
			slotProps={{
				textField: {
					fullWidth: true,
					required: element.required,
					error: errors.length > 0,
					helperText: errorMessage ?? element.description,
					slotProps: {
						inputLabel: { shrink: true },
						htmlInput: {
							'data-cy': `hrr-element-${element.elementId}`,
							'data-element-type': 'date',
						} as React.InputHTMLAttributes<HTMLInputElement>,
					},
				},
			}}
		/>
	);
}
